import { FC, ReactNode } from 'react'

import styled from 'styled-components'
import { useSelector } from 'react-redux'

import LockIcon from 'src/Assets/Svg/pages-icons/Lock.svg?react'
import PaypalLogoIcon from 'src/Assets/Svg/pages-icons/paypal_light.svg?react'
import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import {
  CheckoutBonusTooltip,
  GreenText,
} from 'src/Components/CheckoutBonusTooltip'
import { PaymentMethodEnum, WalletPaymentMethod } from 'src/Constants/enums'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { getResolvedTheme } from 'src/Redux/theme-process'
import { WalletExpressCheckout } from 'src/Components/WalletExpressCheckout'

import { ScheduleSummaryProps } from '..'

const StyledLockIcon = styled(LockIcon)`
  color: ${({ theme }) => theme.color.darkGrey};
`

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    gap: 8px;
    padding: 8px 16px;
    background: ${({ theme }) => theme.color.white};
    border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  }
`
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`
const Text = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`
const PaypalLogo = styled(PaypalLogoIcon)`
  display: block;
  width: 61px;
  height: auto;
`

const ButtonSkeleton = styled(PrimarySkeleton)`
  height: 48px;
  border-radius: 40px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: auto;
    flex: 1 0 auto;
  }
`

const WalletCheckoutWrapper = styled.div<{ $isVisible: boolean }>`
  width: 100%;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    flex: 1 0 auto;
    width: auto;
  }

  ${({ $isVisible }) =>
    $isVisible
      ? ''
      : `
    position: absolute;
    inset: 0 0 auto 0;
    opacity: 0;
    pointer-events: none;
  `}
`

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`

const BonusAmount = styled(GreenText)`
  font-style: italic;
  font-weight: 700;
`

const StyledCTAButton = styled(CTAButton)<{
  $variant?: 'primary' | 'paypal'
  $isDarkTheme?: boolean
}>`
  width: 100%;
  padding-left: 40px;
  padding-right: 40px;

  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: auto;
    flex: 1 0 auto;
  }

  ${({ $variant, theme }) =>
    $variant === 'paypal'
      ? `
    background: ${theme.unchangingColor.paypalBlue};
    border-color: ${theme.unchangingColor.paypalBlue};
    color: ${theme.unchangingColor.white};

    svg path {
      fill: ${theme.unchangingColor.white};
    }

    &:hover,
    &:focus,
    &:focus-visible {
      background: ${theme.unchangingColor.paypalBlueHover};
      border-color: ${theme.unchangingColor.paypalBlueHover};
      color: ${theme.unchangingColor.white};

      svg path {
        fill: ${theme.unchangingColor.white};
      }
    }
  `
      : ''}

  ${({ $variant, $isDarkTheme, theme }) => {
    if ($variant !== 'primary') {
      return ''
    }

    const background = $isDarkTheme
      ? theme.unchangingColor.white
      : theme.unchangingColor.black
    const label = $isDarkTheme
      ? theme.unchangingColor.black
      : theme.unchangingColor.white

    return `
    background: ${background};
    border-color: ${background};
    color: ${label};

    &:hover,
    &:focus,
    &:focus-visible {
      background: ${background};
      border-color: ${background};
      color: ${label};
      opacity: 0.85;
    }
  `
  }}
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }
`
const TotalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    display: none;
  }
`
const TotalTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`
const Total = styled(TotalTitle)`
  font-size: 14px;
  font-weight: 600;
`
const TOTAL_MIN_LENGTH = 5

type Props = Pick<
  ScheduleSummaryProps,
  | 'onConfirm'
  | 'showTooltip'
  | 'countdown'
  | 'total'
  | 'paymentLoading'
  | 'confirmDisabled'
  | 'isBalanceHidden'
  | 'paymentMethod'
  | 'walletCheckout'
> & {
  isLoading?: boolean
}

const PAYPAL_METHODS = [
  PaymentMethodEnum.PAYPAL,
  PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT,
]

const WALLET_METHODS: WalletPaymentMethod[] = [
  PaymentMethodEnum.APPLE_PAY,
  PaymentMethodEnum.GOOGLE_PAY,
]

const getSelectedWalletMethod = (
  paymentMethod?: PaymentMethodEnum,
): WalletPaymentMethod | undefined =>
  WALLET_METHODS.find((method) => method === paymentMethod)

const getButtonContent = (
  isPaypal: boolean,
  enoughBalance: boolean,
): ReactNode => {
  if (enoughBalance) {
    return 'Launch campaign'
  }
  if (isPaypal) {
    return (
      <ButtonContent>
        Pay with
        <PaypalLogo />
      </ButtonContent>
    )
  }
  return 'Confirm & Pay'
}

const getButtonVariant = (
  paymentMethod?: PaymentMethodEnum,
  isPaypal?: boolean,
): 'primary' | 'paypal' | undefined => {
  if (!paymentMethod) {
    return
  }
  if (isPaypal) {
    return 'paypal'
  }
  return 'primary'
}

export const ScheduleSummaryButton: FC<Props> = ({
  onConfirm,
  showTooltip,
  countdown,
  paymentLoading,
  confirmDisabled,
  total,
  isBalanceHidden,
  paymentMethod,
  walletCheckout,
  isLoading,
}) => {
  const resolvedTheme = useSelector(getResolvedTheme)
  const selectedWalletMethod = getSelectedWalletMethod(paymentMethod)

  const enoughBalance =
    total?.includes('0.00') &&
    total.length === TOTAL_MIN_LENGTH &&
    !isBalanceHidden

  const isPaypal =
    !enoughBalance &&
    Boolean(paymentMethod && PAYPAL_METHODS.includes(paymentMethod))

  const buttonContent = getButtonContent(isPaypal, Boolean(enoughBalance))
  const buttonVariant = getButtonVariant(paymentMethod, isPaypal)

  return (
    <Container>
      {showTooltip && (
        <CheckoutBonusTooltip countdown={countdown}>
          Complete your payment now to secure a&nbsp;
          <BonusAmount>$20</BonusAmount>&nbsp;bonus for your next
          campaign&nbsp;🎁
        </CheckoutBonusTooltip>
      )}
      <ButtonWrapper>
        <TotalWrapper>
          <TotalTitle>Total</TotalTitle>
          <Total>{total}</Total>
        </TotalWrapper>
        {walletCheckout && (
          <WalletCheckoutWrapper
            $isVisible={!isLoading && Boolean(selectedWalletMethod)}
          >
            <WalletExpressCheckout
              {...walletCheckout}
              selectedWalletMethod={selectedWalletMethod}
            />
          </WalletCheckoutWrapper>
        )}
        {isLoading && <ButtonSkeleton />}
        {!isLoading && !selectedWalletMethod && (
          <StyledCTAButton
            $variant={buttonVariant}
            $isDarkTheme={resolvedTheme === 'dark'}
            loading={paymentLoading}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {buttonContent}
          </StyledCTAButton>
        )}
      </ButtonWrapper>
      {!enoughBalance && (
        <Wrapper>
          <StyledLockIcon />
          <Text>SSL Secure Payment</Text>
        </Wrapper>
      )}
    </Container>
  )
}
